import styled from '@emotion/styled'
import {
  AiOutlineClose,
  AiOutlineDelete,
  AiOutlineDown,
  AiOutlineMinusCircle,
  AiOutlinePlusCircle,
  AiOutlineRight,
} from 'react-icons/ai'
import { useHistory } from 'react-router'
import { useEffect } from 'react'
import TitleHeader from '../../../components/app-layout/AppHeader/TitleHeader'
import Image from '../../../components/common/Image'
import Text from '../../../components/common/Text'
import PriceFormatter from '../../../components/PriceFormatter'
import CoinFormatter from '../../../components/CoinFormatter'
import { mobile, useScreen } from '../../../utils/responsive-helper'
import * as paths from '../../../constant/path'
import {
  DARK_GRAY,
  FONT_LIGHT_GRAY,
  LIGHT_GRAY,
  SHOP_PRIMARY,
  WHITE,
} from '../../../constant/colors'
import AddCartFooter from '../../Product/components/AddCartFooter'
import {
  useDeleteProductInCart,
  useGetSumPriceInCart,
  useGetUserCart,
  useUpdateProductInCart,
} from '../../../services/cart/cart-query'
import IconTicket from '../../../assets/images/icon-ticket.svg'

const Layout = styled.div`
  padding: 8px 0;
  ${mobile} {
    margin-top: 64px;
    margin-bottom: 88px;
  }
`

const ShopCartStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: calc(100vh - 80px - 90px - 48px);
  padding-top: 24px;
  ${mobile} {
    display: block;
    padding-top: 0px;
  }
`

const CartLayout = styled.div`
  display: flex;
  width: 100%;
  background-color: ${WHITE};
  margin: 8px 0;
  padding: 8px;
`

const ProductImage = styled(Image)``

const ProductDetailMobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: calc(100% - 112px);
  padding-left: 8px;
`

const ProductDetailDeaktopLayout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - 148px);
  padding: 8px;
`

const ProductDetailSection = styled.div`
  width: 100px;
`

const ProductNameLayout = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProductPriceMobileLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const ProductPriceDesktopLayout = styled.div`
  display: flex;
  flex-direction: column;
`

const ManageProductAmountLayout = styled.div`
  display: flex;
  align-items: center;
`

const ManageProductAmountText = styled(Text)`
  line-height: 24px;
  display: flex;
  justify-content: center;
`

const CouponFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${WHITE};
  padding: 14px 16px;
  margin: 8px 0;

  ${mobile} {
    width: 100%;
    position: fixed;
    bottom: 86px;
    margin: 0 -16px;
  }
`

const CouponContent = styled.div`
  display: flex;
`

const ShopCart = () => {
  const history = useHistory()
  const { isMobile } = useScreen()
  const { data: userCart, isLoading, refetch: fetchUserCart } = useGetUserCart()
  const { mutate: updateProductInCart } = useUpdateProductInCart()
  const { mutate: deleteProductInCart } = useDeleteProductInCart()
  const { data: sumPrice, refetch: fetchSumPrice } = useGetSumPriceInCart()

  useEffect(() => {
    fetchUserCart()
  }, [isLoading, fetchUserCart, fetchSumPrice])

  useEffect(() => {
    fetchSumPrice()
  }, [userCart, fetchSumPrice])

  const handleMinusQuantity = (quantity: number, productId: number) => {
    const updateQuantity = quantity - 1
    if (updateQuantity > 0) {
      updateProductInCart({ productId, quantity: updateQuantity })
    }
  }

  const handlePlusQuantity = (quantity: number, productId: number, remain: number) => {
    const updateQuantity = quantity + 1
    const canAdd = remain ? updateQuantity <= remain : false
    if (canAdd) {
      updateProductInCart({ productId, quantity: updateQuantity })
    }
  }

  const handleDeleteProductInCart = (productId: number) => {
    deleteProductInCart({ productId })
  }

  const renderProductCoin = (specialCoin: number) => (
    <ProductDetailSection>
      <CoinFormatter textSize={isMobile ? 14 : 20} value={specialCoin} />
    </ProductDetailSection>
  )

  const renderProductPrice = (normalCoin: number, specialCoin: number) => (
    <ProductDetailSection>
      {normalCoin === 0 ? null : (
        <PriceFormatter
          fontSize={isMobile ? 14 : 24}
          priceType="additional"
          value={normalCoin}
          style={{ lineHeight: '14px' }}
        />
      )}
      <PriceFormatter
        fontSize={isMobile ? 28 : 36}
        lineHeight={isMobile ? 32 : 40}
        priceType="display"
        value={specialCoin}
        style={{ lineHeight: '26px' }}
      />
    </ProductDetailSection>
  )

  const renderProductAmount = (quantity: number, productId: number, remain: number) => (
    <ManageProductAmountLayout>
      <ManageProductAmountText
        color={LIGHT_GRAY}
        size={24}
        onClick={() => handleMinusQuantity(quantity, productId)}
      >
        <AiOutlineMinusCircle />
      </ManageProductAmountText>
      <ManageProductAmountText size={18} color={DARK_GRAY} style={{ width: 36 }}>
        {quantity}
      </ManageProductAmountText>
      <ManageProductAmountText
        color={SHOP_PRIMARY}
        size={24}
        onClick={() => handlePlusQuantity(quantity, productId, remain)}
      >
        <AiOutlinePlusCircle />
      </ManageProductAmountText>
    </ManageProductAmountLayout>
  )

  const handleShopCartCoupons = () => {
    history.push(paths.shopCartCoupons())
  }

  const handleNavigation = () => {
    history.push(paths.shopCartAddresses())
  }

  return (
    <Layout id="shop-cart">
      {isMobile && (
        <TitleHeader
          title={`ตะกร้าสินค้า (${userCart?.length || 0})`}
          icon={<AiOutlineClose />}
          path={paths.shop()}
        />
      )}
      <ShopCartStyled>
        {userCart && (
          <div>
            {userCart?.map((cart, index) => {
              const { product, quantity } = cart
              const { specialCoin, normalCoin, remain } = product
              return (
                <CartLayout key={index}>
                  <ProductImage
                    width={isMobile ? 112 : 148}
                    height={isMobile ? 112 : 148}
                    src={product.thumbnailImage}
                  />
                  {isMobile ? (
                    <ProductDetailMobileLayout>
                      <ProductNameLayout>
                        <Text size={18}>{product.title}</Text>
                        <Text
                          size={22}
                          color={FONT_LIGHT_GRAY}
                          onClick={() => handleDeleteProductInCart(product.id)}
                        >
                          <AiOutlineDelete />
                        </Text>
                      </ProductNameLayout>
                      <ProductPriceMobileLayout>
                        <div>
                          {renderProductPrice(normalCoin, specialCoin)}
                          {renderProductCoin(specialCoin || normalCoin)}
                        </div>
                        {renderProductAmount(quantity, product.id, remain)}
                      </ProductPriceMobileLayout>
                    </ProductDetailMobileLayout>
                  ) : (
                    <ProductDetailDeaktopLayout>
                      <Text size={20} style={{ width: 240 }}>
                        {product.title}
                      </Text>
                      <ProductPriceDesktopLayout>
                        {renderProductPrice(normalCoin, specialCoin)}
                      </ProductPriceDesktopLayout>
                      {renderProductCoin(specialCoin || normalCoin)}
                      {renderProductAmount(quantity, product.id, remain)}
                      <Text
                        size={24}
                        color={FONT_LIGHT_GRAY}
                        onClick={() => handleDeleteProductInCart(product.id)}
                      >
                        <AiOutlineDelete />
                      </Text>
                    </ProductDetailDeaktopLayout>
                  )}
                </CartLayout>
              )
            })}
            <CouponFooter onClick={handleShopCartCoupons}>
              <CouponContent>
                <Image
                  src={IconTicket}
                  width={20}
                  height={15}
                  style={{ margin: 'auto', marginRight: '8px' }}
                />
                <Text size={isMobile ? 16 : 20}>กรอกรหัสส่วนลด</Text>
              </CouponContent>
              <AiOutlineRight />
            </CouponFooter>
          </div>
        )}
        <AddCartFooter
          content="ราคารวม"
          icon={<AiOutlineDown />}
          value={sumPrice ? +sumPrice : 0}
          textButton="สั่งสินค้า"
          onClick={handleNavigation}
        />
      </ShopCartStyled>
    </Layout>
  )
}

export default ShopCart
